import {
  Box,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef } from "react";

import ArchitectureIcon from "@mui/icons-material/Architecture";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";

const steps = [
  {
    id: 1,
    title: "Project Type",
    desc: "What are you building?",
    icon: <ArchitectureIcon fontSize="small" />,
  },
  {
    id: 2,
    title: "Scope & Size",
    desc: "Pages & complexity",
    icon: <LayersOutlinedIcon fontSize="small" />,
  },
  {
    id: 3,
    title: "Features",
    desc: "Auth, payments, APIs",
    icon: <ExtensionOutlinedIcon fontSize="small" />,
  },
  {
    id: 4,
    title: "Estimate",
    desc: "Cost breakdown",
    icon: <PaidOutlinedIcon fontSize="small" />,
  },
];

const EstimatorShell = ({
  step,
  totalSteps = 4,
  title,
  subtitle,
  children,
}) => {
  const theme = useTheme();
  const stepRefs = useRef({});

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const progress = (step / totalSteps) * 100;

  useEffect(() => {
    if (!isMobile) return;

    const activeEl = stepRefs.current[step];
    if (activeEl) {
      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [step, isMobile]);

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 4, md: 4 },
        background:
          "linear-gradient(180deg, #F6F7EB 0%, rgba(246,247,235,0.6) 100%)",
        overflow: "hidden",
      }}
    >
      {/* === Decorative background blobs === */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -160,
          right: -140,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: "rgba(233,79,55,0.08)",
          filter: "blur(90px)",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -180,
          left: -160,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background: "rgba(15,58,66,0.08)",
          filter: "blur(100px)",
        }}
      />

      <Container maxWidth="lg">
        {/* ===== HEADER ===== */}
        <Stack
          spacing={2}
          textAlign="center"
          sx={{
            mb: { xs: 5, md: 7 },
            maxWidth: 760,
            mx: "auto",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "var(--color-midnight)",
            }}
          >
            Project{" "}
            <Box component="span" sx={{ color: "var(--color-fiery)" }}>
              Cost Estimator
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              color: "var(--color-midnight-70)",
            }}
          >
            Answer a few guided questions to receive a realistic development
            estimate based on real-world benchmarks and delivery experience.
          </Typography>
        </Stack>

        {/* ===== MAIN CARD ===== */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, md: 4 },
            p: { xs: 3, sm: 4, md: 6 },
            backgroundColor: "#fff",
            boxShadow:
              "0 30px 70px rgba(0,0,0,0.08), 0 8px 20px rgba(0,0,0,0.05)",
          }}
        >
          {/* ===== STEP NAV ===== */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              mb: 4,
              overflowX: { xs: "auto", sm: "visible" },
              pb: { xs: 1, sm: 0 },
            }}
          >
            {steps.map((s) => {
              const active = s.id === step;
              const completed = s.id < step;

              return (
                <Box
                  key={s.id}
                  ref={(el) => (stepRefs.current[s.id] = el)}
                  sx={{
                    flex: { xs: "0 0 240px", sm: 1 },
                    px: 2,
                    py: 1.8,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor:
                      active || completed
                        ? "var(--color-fiery)"
                        : "var(--color-midnight-15)",
                    backgroundColor: active ? "rgba(233,79,55,0.08)" : "#fff",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    scrollSnapAlign: "center", // ⭐ optional but nice
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor:
                        completed || active
                          ? "var(--color-fiery)"
                          : "var(--color-midnight-15)",
                      color: completed || active ? "#fff" : "#555",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                      {s.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: "var(--color-midnight-70)",
                      }}
                    >
                      {s.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {/* ===== STEP TITLE ===== */}
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--color-midnight-70)",
                fontWeight: 700,
              }}
            >
              Step {step} of {totalSteps}
            </Typography>

            {/* <Typography
              sx={{
                mt: 0.5,
                fontSize: { xs: "1.3rem", md: "1.6rem" },
                fontWeight: 700,
                color: "var(--color-midnight)",
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "0.95rem",
                  color: "var(--color-midnight-70)",
                }}
              >
                {subtitle}
              </Typography>
            )} */}
          </Box>

          {/* ===== PROGRESS ===== */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 999,
              mb: { xs: 3, md: 4 },
              backgroundColor: "var(--color-midnight-15)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "var(--color-fiery)",
              },
            }}
          />

          {/* ===== CONTENT ===== */}
          <Box>{children}</Box>

          {/* ===== FOOTER NOTE ===== */}
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              p: { xs: 2.5, md: 3 },
              borderRadius: 2,

              background: `
      linear-gradient(
        180deg,
        rgba(15,58,66,0.04),
        rgba(15,58,66,0.02)
      )
    `,

              border: "1px solid rgba(15,58,66,0.08)",

              boxShadow:
                "0 8px 24px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",

              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              flexDirection:{xs: "column",md:"row"}
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "rgba(233,79,55,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "var(--color-fiery)",
                }}
              />
            </Box>

            {/* Content */}
            <Box>
              {/* Title */}
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  color: "var(--color-midnight)",
                  mb: 0.5,
                }}
              >
                Pricing Guidance & Disclaimer
              </Typography>

              {/* Guidance */}
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "var(--color-midnight-80)",
                  lineHeight: 1.6,
                  mb: 1,
                }}
              >
                The estimate shown includes design, development, quality
                assurance, and deployment efforts based on your selected scope
                and features. Costs may vary depending on third-party services,
                integrations, compliance requirements, and delivery timelines.
              </Typography>

              {/* Legal note */}
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "var(--color-midnight-60)",
                  lineHeight: 1.6,
                  fontStyle: "italic",
                }}
              >
                This tool provides indicative pricing for planning purposes only
                and does not constitute a formal quotation, proposal, or legally
                binding agreement. Final pricing is subject to detailed
                requirements analysis and contractual terms.
              </Typography>
            </Box>
          </Box>

          {/* <Box
            sx={{
              mt: { xs: 4, md: 5 },
              p: 2.5,
              borderRadius: 2,
              backgroundColor: "rgba(15,58,66,0.04)",
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 18, mt: "2px", color: "var(--color-midnight)" }}
            />

            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Pricing guidance
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "var(--color-midnight-70)",
                }}
              >
                Estimates include design, development, testing, and deployment.
                Final pricing may vary based on integrations, third-party
                services, and delivery timelines. This is an indicative estimate
                only and not a binding quotation.
              </Typography>
            </Box>
          </Box> */}
        </Paper>
      </Container>
    </Box>
  );
};

export default EstimatorShell;
